/**
 * User Profile Page (DC-896, DC-904)
 *
 * Displays user information, allows password changes, and MFA management.
 */

import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useAuthenticatedFetch } from '../../hooks/useAuth';
import { PasswordStrengthIndicator, isPasswordValid } from '../../components/auth/PasswordStrengthIndicator';
import { MFASetup } from '../../components/auth/MFASetup';
import { BackupCodesDisplay } from '../../components/auth/BackupCodesDisplay';
import '../../styles/auth.css';

interface MFAStatus {
  isEnabled: boolean;
  isVerified: boolean;
  enabledAt: string | null;
  lastUsedAt: string | null;
  hasRecoveryEmail: boolean;
  backupCodesRemaining: number;
}

export function Profile() {
  const navigate = useNavigate();
  const { user, logout, isLoading } = useAuth();
  const { authFetch } = useAuthenticatedFetch();

  const [activeTab, setActiveTab] = useState<'info' | 'password' | 'security'>('info');

  // MFA state
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [disablingMFA, setDisablingMFA] = useState(false);
  const [disableToken, setDisableToken] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Get initials for avatar
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return parts[0][0] + parts[1][0];
    }
    return name.substring(0, 2);
  };

  // Fetch MFA status when security tab is active
  useEffect(() => {
    if (activeTab === 'security' && user && !mfaStatus && !mfaLoading) {
      fetchMFAStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only trigger when tab or user changes
  }, [activeTab, user]);

  const fetchMFAStatus = async () => {
    setMfaLoading(true);
    setMfaError(null);
    try {
      const response = await authFetch('/api/iam/mfa/status');
      const data = await response.json();
      if (response.ok) {
        setMfaStatus(data.data.mfa);
      } else {
        setMfaError(data.error?.message || 'Failed to load MFA status');
      }
    } catch {
      setMfaError('Network error. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMFASetupComplete = () => {
    setShowMFASetup(false);
    fetchMFAStatus(); // Refresh status after setup
  };

  const handleDisableMFA = async () => {
    if (disableToken.length !== 6) {
      setMfaError('Please enter a 6-digit code');
      return;
    }

    setMfaError(null);
    setDisablingMFA(true);

    try {
      const response = await authFetch('/api/iam/mfa/disable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: disableToken }),
      });

      const data = await response.json();

      if (response.ok) {
        setMfaStatus(null);
        setDisableToken('');
        fetchMFAStatus(); // Refresh status
      } else {
        setMfaError(data.error?.message || 'Failed to disable MFA');
      }
    } catch {
      setMfaError('Network error. Please try again.');
    } finally {
      setDisablingMFA(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const token = prompt('Enter your 6-digit authenticator code to regenerate backup codes:');
    if (!token || token.length !== 6) {
      setMfaError('Please enter a valid 6-digit code');
      return;
    }

    setMfaError(null);
    setMfaLoading(true);

    try {
      const response = await authFetch('/api/iam/mfa/regenerate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        setBackupCodes(data.data.backupCodes);
        setShowBackupCodes(true);
        fetchMFAStatus(); // Refresh status
      } else {
        setMfaError(data.error?.message || 'Failed to regenerate backup codes');
      }
    } catch {
      setMfaError('Network error. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handlePasswordChange = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setPasswordError('New password does not meet the requirements');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setChangingPassword(true);

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('compass_access_token')}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setPasswordError(data.error?.message || 'Failed to change password');
      } else {
        setPasswordSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch {
      setPasswordError('Network error. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-logo">
              <div className="auth-logo-text">DATA COMPASS</div>
            </div>
            <p style={{ textAlign: 'center', color: 'var(--muted)' }}>
              Please log in to view your profile.
            </p>
            <Link
              to="/login"
              className="auth-button"
              style={{ textDecoration: 'none', display: 'block', textAlign: 'center', marginTop: '1rem' }}
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" style={{ background: 'var(--bg-secondary)' }}>
      <div className="auth-container" style={{ maxWidth: '600px' }}>
        {/* Back button */}
        <div style={{ marginBottom: '1rem' }}>
          <Link
            to="/home"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--muted)',
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}
          >
            <span>&larr;</span> Back to Home
          </Link>
        </div>

        <div className="auth-card" style={{ padding: '2rem' }}>
          {/* Header with avatar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              marginBottom: '2rem',
              paddingBottom: '1.5rem',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--primary) 0%, #1d4ed8 100%)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {getInitials(user.name).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>{user.name}</h1>
              <p style={{ margin: '0.25rem 0 0', color: 'var(--muted)' }}>{user.email}</p>
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    background:
                      user.role === 'admin'
                        ? 'rgba(220, 38, 38, 0.1)'
                        : user.role === 'analyst'
                          ? 'rgba(59, 130, 246, 0.1)'
                          : 'rgba(107, 114, 128, 0.1)',
                    color:
                      user.role === 'admin'
                        ? 'var(--danger)'
                        : user.role === 'analyst'
                          ? 'var(--primary)'
                          : 'var(--muted)',
                  }}
                >
                  {user.role}
                </span>
                <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                  @ {user.organization.name}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '1.5rem',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <button
              onClick={() => setActiveTab('info')}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.75rem 0',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                color: activeTab === 'info' ? 'var(--primary)' : 'var(--muted)',
                borderBottom: activeTab === 'info' ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              Profile Information
            </button>
            <button
              onClick={() => setActiveTab('password')}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.75rem 0',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                color: activeTab === 'password' ? 'var(--primary)' : 'var(--muted)',
                borderBottom: activeTab === 'password' ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              Change Password
            </button>
            <button
              onClick={() => setActiveTab('security')}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.75rem 0',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                color: activeTab === 'security' ? 'var(--primary)' : 'var(--muted)',
                borderBottom: activeTab === 'security' ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              Security
            </button>
          </div>

          {/* Tab content */}
          {activeTab === 'info' && (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'var(--muted)',
                    marginBottom: '0.25rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Full Name
                </label>
                <div style={{ fontSize: '1rem', fontWeight: 500 }}>{user.name}</div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'var(--muted)',
                    marginBottom: '0.25rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Email Address
                </label>
                <div style={{ fontSize: '1rem' }}>{user.email}</div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'var(--muted)',
                    marginBottom: '0.25rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Organization
                </label>
                <div style={{ fontSize: '1rem' }}>{user.organization.name}</div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'var(--muted)',
                    marginBottom: '0.25rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Role
                </label>
                <div style={{ fontSize: '1rem', textTransform: 'capitalize' }}>{user.role}</div>
              </div>

              <div
                style={{
                  marginTop: '2rem',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid var(--border)',
                }}
              >
                <button
                  onClick={handleLogout}
                  className="auth-button"
                  style={{
                    background: 'var(--danger)',
                    width: 'auto',
                    padding: '0.75rem 1.5rem',
                  }}
                  disabled={isLoading}
                >
                  Sign Out
                </button>
              </div>
            </div>
          )}

          {activeTab === 'password' && (
            <form onSubmit={handlePasswordChange}>
              {passwordError && (
                <div className="auth-error" style={{ marginBottom: '1rem' }}>
                  <span className="auth-error-icon">!</span>
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="auth-success" style={{ marginBottom: '1rem' }}>
                  <span>&#10003;</span>
                  Password changed successfully!
                </div>
              )}

              <div className="auth-field">
                <label htmlFor="currentPassword" className="auth-label">
                  Current Password
                </label>
                <div className="auth-input-wrapper">
                  <input
                    id="currentPassword"
                    type={showPasswords ? 'text' : 'password'}
                    className="auth-input"
                    placeholder="Enter your current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={changingPassword}
                  />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="newPassword" className="auth-label">
                  New Password
                </label>
                <div className="auth-input-wrapper">
                  <input
                    id="newPassword"
                    type={showPasswords ? 'text' : 'password'}
                    className="auth-input"
                    placeholder="Create a strong password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={changingPassword}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPasswords(!showPasswords)}
                    tabIndex={-1}
                    aria-label={showPasswords ? 'Hide passwords' : 'Show passwords'}
                  >
                    {showPasswords ? '\uD83D\uDC41' : '\uD83D\uDC41\u200D\uD83D\uDDE8'}
                  </button>
                </div>
                <PasswordStrengthIndicator password={newPassword} />
              </div>

              <div className="auth-field">
                <label htmlFor="confirmNewPassword" className="auth-label">
                  Confirm New Password
                </label>
                <div className="auth-input-wrapper">
                  <input
                    id="confirmNewPassword"
                    type={showPasswords ? 'text' : 'password'}
                    className={`auth-input ${newPassword !== confirmPassword && confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={changingPassword}
                  />
                </div>
                {newPassword !== confirmPassword && confirmPassword && (
                  <div className="auth-field-error">Passwords do not match</div>
                )}
              </div>

              <button type="submit" className="auth-button" disabled={changingPassword}>
                {changingPassword ? (
                  <>
                    <span className="auth-button-spinner" />
                    Changing Password...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </form>
          )}

          {activeTab === 'security' && (
            <div className="mfa-section">
              {/* MFA Setup Modal */}
              {showMFASetup && (
                <MFASetup
                  onComplete={handleMFASetupComplete}
                  onCancel={() => setShowMFASetup(false)}
                />
              )}

              {/* Backup Codes Modal */}
              {showBackupCodes && backupCodes.length > 0 && (
                <BackupCodesDisplay
                  backupCodes={backupCodes}
                  onContinue={() => {
                    setShowBackupCodes(false);
                    setBackupCodes([]);
                  }}
                  isInitialSetup={false}
                />
              )}

              {/* Main Security Content */}
              {!showMFASetup && !showBackupCodes && (
                <>
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.125rem', fontWeight: 600 }}>
                    Two-Factor Authentication
                  </h3>
                  <p style={{ margin: '0 0 1.5rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
                    Add an extra layer of security to your account by requiring a verification code in addition to your password.
                  </p>

                  {mfaError && (
                    <div className="auth-error" style={{ marginBottom: '1rem' }}>
                      <span className="auth-error-icon">!</span>
                      {mfaError}
                    </div>
                  )}

                  {mfaLoading && !mfaStatus && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}>
                      <div className="auth-button-spinner" style={{ width: '24px', height: '24px', margin: '0 auto 1rem' }} />
                      Loading MFA status...
                    </div>
                  )}

                  {mfaStatus && (
                    <div className="mfa-status-card">
                      <div className="mfa-status-header">
                        <div className="mfa-status-icon" data-enabled={mfaStatus.isEnabled}>
                          {mfaStatus.isEnabled ? (
                            <span style={{ color: 'var(--success)' }}>&#10003;</span>
                          ) : (
                            <span style={{ color: 'var(--muted)' }}>&#128274;</span>
                          )}
                        </div>
                        <div className="mfa-status-info">
                          <div className="mfa-status-title">
                            {mfaStatus.isEnabled ? 'MFA Enabled' : 'MFA Not Enabled'}
                          </div>
                          <div className="mfa-status-subtitle">
                            {mfaStatus.isEnabled
                              ? `Enabled ${mfaStatus.enabledAt ? new Date(mfaStatus.enabledAt).toLocaleDateString() : 'recently'}`
                              : 'Your account is not protected with two-factor authentication'}
                          </div>
                        </div>
                      </div>

                      {mfaStatus.isEnabled ? (
                        <div className="mfa-enabled-options">
                          <div className="mfa-details-grid">
                            <div className="mfa-detail-row">
                              <span className="mfa-detail-label">Backup Codes Remaining:</span>
                              <span
                                className="mfa-detail-value"
                                style={{
                                  color: mfaStatus.backupCodesRemaining <= 2 ? 'var(--danger)' : 'var(--success)',
                                  fontWeight: 600,
                                }}
                              >
                                {mfaStatus.backupCodesRemaining}
                                {mfaStatus.backupCodesRemaining <= 2 && (
                                  <span style={{ color: 'var(--danger)', fontSize: '0.75rem', marginLeft: '0.5rem', fontWeight: 400 }}>
                                    (Low - consider regenerating)
                                  </span>
                                )}
                              </span>
                            </div>
                            {mfaStatus.lastUsedAt && (
                              <div className="mfa-detail-row">
                                <span className="mfa-detail-label">Last Used:</span>
                                <span className="mfa-detail-value">
                                  {new Date(mfaStatus.lastUsedAt).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="mfa-actions" style={{ marginTop: '1.5rem' }}>
                            <button
                              type="button"
                              className="auth-button auth-button-secondary"
                              onClick={handleRegenerateBackupCodes}
                              disabled={mfaLoading}
                              style={{ marginRight: '1rem' }}
                            >
                              Regenerate Backup Codes
                            </button>
                          </div>

                          <div
                            className="mfa-disable-section"
                            style={{
                              marginTop: '2rem',
                              paddingTop: '1.5rem',
                              borderTop: '1px solid var(--border)',
                            }}
                          >
                            <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                              Disable Two-Factor Authentication
                            </h4>
                            <p style={{ margin: '0 0 1rem', color: 'var(--muted)', fontSize: '0.75rem' }}>
                              Enter your authenticator code to disable MFA. This will make your account less secure.
                            </p>
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                              <input
                                type="text"
                                className="auth-input"
                                placeholder="6-digit code"
                                value={disableToken}
                                onChange={(e) => setDisableToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                maxLength={6}
                                style={{ width: '120px' }}
                                disabled={disablingMFA}
                              />
                              <button
                                type="button"
                                className="auth-button"
                                onClick={handleDisableMFA}
                                disabled={disablingMFA || disableToken.length !== 6}
                                style={{
                                  background: 'var(--danger)',
                                  padding: '0.75rem 1rem',
                                }}
                              >
                                {disablingMFA ? 'Disabling...' : 'Disable MFA'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mfa-enable-section" style={{ marginTop: '1.5rem' }}>
                          <button
                            type="button"
                            className="auth-button"
                            onClick={() => setShowMFASetup(true)}
                          >
                            Enable Two-Factor Authentication
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {!mfaLoading && !mfaStatus && (
                    <div className="mfa-enable-section">
                      <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
                        Unable to load MFA status. Please try again.
                      </p>
                      <button
                        type="button"
                        className="auth-button auth-button-secondary"
                        onClick={fetchMFAStatus}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
